import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Icons } from '../lib/Icons';
import { runTherapistReportV2Synthesis, uploadSupportingReport } from '../lib/api';

const REPORT_TYPE_OPTIONS = [
  { value: 'occupational_therapy', label: 'Occupational Therapy (OT)' },
  { value: 'educational_psychology', label: 'Educational Psychologist (EP)' },
  { value: 'speech_language', label: 'Speech and Language' },
  { value: 'other', label: 'Other professional report' },
];

export default function TherapistReportUploader({ studentId, onProcessingComplete }) {
  const [file, setFile] = useState(null);
  const [reportType, setReportType] = useState('occupational_therapy');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [summary, setSummary] = useState(null);

  const handleUpload = async () => {
    if (!studentId || !file) {
      setMessage('Choose a PDF report before uploading.');
      return;
    }
    if (!file.name?.toLowerCase?.().endsWith('.pdf')) {
      toast.error('Only PDF reports are supported');
      return;
    }

    try {
      setBusy(true);
      setMessage('Uploading therapist report...');
      const uploaded = await uploadSupportingReport(studentId, reportType, file);
      setMessage('Extracting report evidence and updating profile...');

      const synthesized = await runTherapistReportV2Synthesis({
        studentId,
        storagePath: uploaded.storagePath,
        fileSizeBytes: file.size,
        mimeType: file.type || 'application/pdf',
      });

      setSummary({
        reportId: uploaded.reportId,
        reportType,
        jobId: synthesized?.job?.jobId || null,
        needsDetected: (synthesized?.extraction?.therapistReport?.identifiedNeeds || []).length,
      });
      setMessage('Therapist report processed successfully.');
      toast.success('Therapist report ingested');
      onProcessingComplete?.(synthesized);
    } catch (error) {
      console.error('Error uploading therapist report:', error);
      setMessage(`Failed to process report: ${error?.message || 'Unknown error'}`);
      toast.error('Failed to ingest therapist report');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-4">
      <h3 className="flex items-center text-lg font-semibold text-indigo-900">
        <Icons.Document className="mr-2 h-5 w-5" />
        Upload Therapist Report (Non-EHCP and EHCP students)
      </h3>
      <p className="mt-1 text-sm text-indigo-800">
        Upload OT/EP/SLT reports to inform non-diagnostic needs, classroom strategies, and intervention recommendations.
      </p>

      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <label className="text-sm">
          <span className="mb-1 block font-medium text-gray-700">Report type</span>
          <select
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
            disabled={busy}
          >
            {REPORT_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <span className="mb-1 block font-medium text-gray-700">PDF report</span>
          <input
            type="file"
            accept="application/pdf"
            className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            disabled={busy}
          />
        </label>
      </div>

      <button
        type="button"
        className="mt-4 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
        disabled={!file || busy}
        onClick={handleUpload}
      >
        {busy ? 'Processing...' : 'Upload and Extract Evidence'}
      </button>

      {message && <p className="mt-3 text-sm text-gray-700">{message}</p>}

      {summary && (
        <div className="mt-3 rounded-md border border-indigo-200 bg-white p-3 text-sm text-gray-700">
          <p className="font-medium text-gray-900">Latest therapist report result</p>
          <p>Report reference: {summary.reportId}</p>
          <p>Document job: {summary.jobId || 'n/a'}</p>
          <p>Identified needs extracted: {summary.needsDetected}</p>
        </div>
      )}
    </div>
  );
}
